import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { companyAPI, leaveAPI, employeeAPI, attendanceAPI, payrollAPI, feedAPI, settingsAPI } from '../../lib/api';
import { getLocation, getIP, checkGeofence, formatDistance } from '../../lib/location';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Users, CalendarDays, TrendingUp, UserPlus, Loader2, IndianRupee, Cake, PartyPopper, Briefcase, CheckCircle2, User, Settings, Clock, MapPin, Plus, Heart, Baby, Umbrella, Plane, Activity, Download, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { formatIndianCurrency, cn } from '../../lib/utils';


const Dashboard = () => {
  const { user, isAdminOrHR, isEmployee } = useAuth();
  const [stats, setStats] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [recentLeaves, setRecentLeaves] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [latestPayslip, setLatestPayslip] = useState(null);
  const [showSalary, setShowSalary] = useState(false);
  const [newHires, setNewHires] = useState([]);
  const [celebrations, setCelebrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPunching, setIsPunching] = useState(false);
  const [allowedLocations, setAllowedLocations] = useState([]);
  const [punchModal, setPunchModal] = useState({
    isOpen: false,
    state: 'fetching', // 'fetching', 'success', 'error'
    message: '',
    type: 'in' // 'in' or 'out'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      if (isAdminOrHR()) {
        const [statsRes, holidaysRes, feedsRes] = await Promise.all([
          companyAPI.getDashboardStats(),
          leaveAPI.getHolidays(new Date().getFullYear()),
          feedAPI.getFeeds()
        ]);
        setStats(statsRes.data);
        setHolidays(holidaysRes.data);
        setFeeds(feedsRes.data?.posts || feedsRes.data || []);
      } else if (isEmployee()) {
        const employeeId = user?.employee_id;

        const results = await Promise.allSettled([
          employeeId ? leaveAPI.getBalance(employeeId) : Promise.reject('No ID'),
          attendanceAPI.getStatus(),
          leaveAPI.getHolidays(new Date().getFullYear()),
          feedAPI.getFeeds(),
          payrollAPI.getMyPayslips(),
          employeeAPI.getDirectory({ page_size: 5 }),
          employeeAPI.getCelebrations(),
          employeeId ? leaveAPI.getRequests({ employee_id: employeeId, limit: 5 }) : Promise.resolve({ data: [] }),
          employeeId ? employeeAPI.getEmployee(employeeId) : Promise.reject('No ID'),
          settingsAPI.getLocations()
        ]);

        const [leaveRes, attendanceRes, holidaysRes, feedsRes, payslipsRes, hiresRes, celebRes, recentLeavesRes, empRes, locRes] = results;

        if (leaveRes.status === 'fulfilled') setLeaveBalance(leaveRes.value.data);
        if (attendanceRes.status === 'fulfilled') setAttendanceStatus(attendanceRes.value.data);
        if (holidaysRes.status === 'fulfilled') setHolidays(holidaysRes.value.data);
        if (feedsRes.status === 'fulfilled') setFeeds(feedsRes.value.data?.posts || feedsRes.value.data || []);
        if (payslipsRes.status === 'fulfilled' && payslipsRes.value.data?.length > 0) {
          setLatestPayslip(payslipsRes.value.data[0]);
        }
        if (hiresRes.status === 'fulfilled') setNewHires(hiresRes.value.data?.employees || []);
        if (celebRes.status === 'fulfilled') setCelebrations(celebRes.value.data || []);
        if (recentLeavesRes.status === 'fulfilled') setRecentLeaves(recentLeavesRes.value.data);

        // Fetch allowed locations for geofencing
        if (empRes.status === 'fulfilled' && locRes.status === 'fulfilled') {
          const assignedNames = empRes.value.data.work_locations || [];
          const filtered = locRes.value.data.filter(l => assignedNames.includes(l.name));
          setAllowedLocations(filtered);
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePunch = async (type) => {
    setPunchModal({ isOpen: true, state: 'fetching', message: 'Fetching your location...', type: type });
    setIsPunching(true);
    try {
      const location = await getLocation();
      const ip = await getIP();

      setPunchModal(prev => ({ ...prev, message: 'Validating geofence...' }));

      // Geofence check
      if (allowedLocations.length > 0) {
        const geoCheck = checkGeofence(location, allowedLocations);
        if (!geoCheck.allowed) {
          const errorMsg = `You are outside the permitted work area. Nearest location: ${geoCheck.nearestLocation} (${formatDistance(geoCheck.distance)} away)`;
          setPunchModal({ isOpen: true, state: 'error', message: errorMsg, type: type });
          setIsPunching(false);
          return;
        }
      }

      setPunchModal(prev => ({ ...prev, message: 'Recording attendance...' }));

      if (type === 'in') {
        const res = await attendanceAPI.checkIn({
          ...location,
          ip_address: ip
        });
        setAttendanceStatus(res.data);
        setPunchModal({ isOpen: true, state: 'success', message: 'Successfully checked in!', type: 'in' });
      } else {
        const res = await attendanceAPI.checkOut({
          ...location,
          ip_address: ip
        });
        setAttendanceStatus(res.data);
        setPunchModal({ isOpen: true, state: 'success', message: 'Successfully checked out!', type: 'out' });
      }
      fetchData();
    } catch (error) {
      const errorMsg = error.message || `Failed to punch ${type}. Please ensure location is enabled.`;
      setPunchModal({ isOpen: true, state: 'error', message: errorMsg, type: type });
    } finally {
      setIsPunching(false);
    }
  };

  const handleDownloadPayslip = async (runId) => {
    try {
      const response = await payrollAPI.downloadPayslipPdf(user.employee_id, runId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Payslip_${user.employee_id}_${runId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download payslip');
    }
  };

  const getUpcomingHolidays = () => {
    const today = new Date();
    const thirtyDaysLater = new Date();
    thirtyDaysLater.setDate(today.getDate() + 30);
    today.setHours(0, 0, 0, 0);
    return holidays
      .filter(h => {
        const d = new Date(h.date);
        return d >= today && d <= thirtyDaysLater;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };




  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Punch Status Modal */}
      <Dialog open={punchModal.isOpen} onOpenChange={(open) => !isPunching && setPunchModal(prev => ({ ...prev, isOpen: open }))}>
        <DialogContent className="sm:max-w-[400px] text-center py-10 z-[100]">
          <div className="flex flex-col items-center justify-center space-y-6">
            {punchModal.state === 'fetching' ? (
              <>
                <div className="relative">
                  <div className="h-20 w-20 rounded-full border-4 border-primary/20 animate-pulse" />
                  <Loader2 className="h-10 w-10 text-primary animate-spin absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                </div>
                <div className="space-y-2">
                  <DialogTitle className="text-xl">Processing Punch {punchModal.type === 'in' ? 'In' : 'Out'}</DialogTitle>
                  <DialogDescription className="text-base">{punchModal.message}</DialogDescription>
                </div>
              </>
            ) : punchModal.state === 'success' ? (
              <>
                <div className="h-20 w-20 rounded-full bg-green-50 flex items-center justify-center">
                  <CheckCircle className="h-12 w-12 text-green-500" />
                </div>
                <div className="space-y-2">
                  <DialogTitle className="text-xl text-green-700">Punch Successful!</DialogTitle>
                  <DialogDescription className="text-base text-green-600/80">{punchModal.message}</DialogDescription>
                </div>
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700" 
                  onClick={() => setPunchModal(prev => ({ ...prev, isOpen: false }))}
                >
                  Dismiss
                </Button>
              </>
            ) : (
              <>
                <div className="h-20 w-20 rounded-full bg-red-50 flex items-center justify-center">
                  <XCircle className="h-12 w-12 text-red-500" />
                </div>
                <div className="space-y-2">
                  <DialogTitle className="text-xl text-red-700">Punch Failed</DialogTitle>
                  <DialogDescription className="text-base text-red-600/80">{punchModal.message}</DialogDescription>
                </div>
                <Button 
                  variant="outline"
                  className="w-full border-red-200 text-red-700 hover:bg-red-50" 
                  onClick={() => setPunchModal(prev => ({ ...prev, isOpen: false }))}
                >
                  Try Again
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
          Welcome back, {user?.first_name}!
        </h1>
        <p className="text-muted-foreground mt-1 text-sm sm:text-base">
          Here's what's happening in your organization today.
        </p>
      </div>

      {isAdminOrHR() && stats && (
        <div className="grid gap-6">
          {/* Main Stats Row */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Total Employees</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total_employees}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.recent_hires} joined this month
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Active Staff</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.active_employees}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  On payroll
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Monthly Payroll</CardTitle>
                <IndianRupee className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatIndianCurrency(stats.payroll?.monthly_total || 0)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Estimated cost
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Gender Mix</CardTitle>
                <Briefcase className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.gender_distribution?.Male || 0}M / {stats.gender_distribution?.Female || 0}F
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Distribution ratio
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* Today's Attendance */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Today's Attendance</CardTitle>
                <CardDescription>Real-time punch status</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Present</span>
                    <span className="font-bold">{stats.attendance_today?.present || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Late Arrivals</span>
                    <span className="font-bold">{stats.attendance_today?.late || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm border-t pt-2">
                    <span className="text-muted-foreground font-medium">Absent / No Punch</span>
                    <span className="font-bold">{stats.attendance_today?.absent || 0}</span>
                  </div>
                </div>
                <div className="mt-4">
                  <Link to="/attendance" className="text-xs font-medium text-primary hover:underline flex items-center gap-1">
                    View list <TrendingUp className="h-3 w-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Pending Approvals / Tasks */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Action Items</CardTitle>
                <CardDescription>Tasks requiring attention</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  <Link to="/leave" className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <CalendarDays className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Leave Requests</span>
                    </div>
                    {stats.pending_leaves > 0 && <Badge variant="secondary">{stats.pending_leaves}</Badge>}
                  </Link>

                  <Link to="/onboarding" className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <UserPlus className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Onboarding</span>
                    </div>
                    {stats.pending_onboarding > 0 && <Badge variant="secondary">{stats.pending_onboarding}</Badge>}
                  </Link>

                  <Link to="/tax" className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Tax Verifications</span>
                    </div>
                    {stats.pending_tax > 0 && <Badge variant="secondary">{stats.pending_tax}</Badge>}
                  </Link>
                </div>
              </CardContent>
            </Card>

            {/* Celebrations */}
            <Card className="border-none shadow-md bg-white">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Cake className="h-5 w-5 text-pink-500" />
                  Upcoming Celebrations
                </CardTitle>
                <CardDescription>Next 30 days</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Birthdays section */}
                  {stats.upcoming_birthdays?.length > 0 && (
                    <div className="space-y-3">
                      {stats.upcoming_birthdays.map((bday, i) => (
                        <div key={`bday-${i}`} className="flex items-center justify-between p-2.5 rounded-lg bg-pink-50/30 border border-pink-50/50 hover:bg-pink-50/50 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-white border border-pink-100 flex items-center justify-center text-pink-500 font-bold text-xs">
                              {bday.name[0]}
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm font-semibold text-slate-800">{bday.name}</span>
                              <span className="text-[10px] text-pink-600 font-medium uppercase tracking-wider">Birthday</span>
                            </div>
                          </div>
                          <span className="text-xs font-bold text-slate-500">
                            {new Date(bday.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Anniversaries section */}
                  {stats.upcoming_anniversaries?.length > 0 && (
                    <div className={cn("space-y-3", stats.upcoming_birthdays?.length > 0 && "pt-2 border-t border-slate-100")}>
                      {stats.upcoming_anniversaries.map((anniv, i) => (
                        <div key={`anniv-${i}`} className="flex items-center justify-between p-2.5 rounded-lg bg-indigo-50/30 border border-indigo-50/50 hover:bg-indigo-50/50 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-white border border-indigo-100 flex items-center justify-center text-indigo-500 font-bold text-xs">
                              {anniv.name[0]}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-slate-800">{anniv.name}</span>
                              <Badge variant="secondary" className="text-[9px] px-2 h-4 rounded-full border-none bg-indigo-100 text-indigo-700 font-bold">
                                {anniv.years}Y
                              </Badge>
                            </div>
                          </div>
                          <span className="text-xs font-bold text-slate-500">
                            {new Date(anniv.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {!stats.upcoming_birthdays?.length && !stats.upcoming_anniversaries?.length && (
                    <div className="py-8 text-center bg-slate-50 rounded-lg border border-dashed border-slate-200">
                      <p className="text-xs text-muted-foreground italic">No upcoming celebrations</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-7">
            {/* Department Distribution */}
            <Card className="lg:col-span-4">
              <CardHeader>
                <CardTitle>Department Breakdown</CardTitle>
                <CardDescription>Employee count per department</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-5 max-h-[290px] overflow-y-auto pr-4 -mr-4 custom-scrollbar">
                  {stats.departments?.length > 0 ? (
                    stats.departments.map((dept) => (
                      <div key={dept.name}>
                        <div className="flex items-center justify-between text-sm mb-1.5">
                          <span className="font-medium text-slate-700">{dept.name}</span>
                          <span className="text-slate-500">{dept.count}</span>
                        </div>
                        <div className="h-1.5 w-full bg-slate-100/80 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all duration-1000"
                            style={{ 
                              width: stats.active_employees > 0 
                                ? `${(dept.count / stats.active_employees) * 100}%` 
                                : '0%' 
                            }}
                          />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="py-8 text-center text-muted-foreground italic text-sm">
                      No departments found
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Upcoming Holidays */}
            <Card className="lg:col-span-3">
              <CardHeader>
                <CardTitle>Upcoming Holidays</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y px-6">
                  {getUpcomingHolidays().map((holiday) => (
                    <div key={holiday.id} className="py-4 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold">{holiday.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(holiday.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-[10px]">{holiday.is_optional ? 'Optional' : 'Mandatory'}</Badge>
                    </div>
                  ))}
                  {getUpcomingHolidays().length === 0 && (
                    <p className="p-6 text-sm text-muted-foreground text-center">No upcoming holidays</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Employee Dashboard */}
      {isEmployee() && (
        <div className="grid gap-6">
          {/* Top Row: Attendance & Celebrations */}
          <div className="grid gap-6 lg:grid-cols-12">
            {/* Attendance Punch - Left 5 cols on desktop */}
            <Card className="lg:col-span-5 shadow-sm border-slate-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="h-5 w-5 text-primary" />
                    Attendance Today
                  </CardTitle>
                  {attendanceStatus?.check_in && !attendanceStatus?.check_out && (
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-none animate-pulse">On Clock</Badge>
                  )}
                </div>
                <CardDescription>
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {!attendanceStatus?.check_in ? (
                  <div className="text-center py-6">
                    <Button onClick={() => handlePunch('in')} disabled={isPunching} className="w-full">
                      {isPunching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Check In
                    </Button>
                  </div>
                ) : !attendanceStatus?.check_out ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Check In</p>
                        <p className="text-xl font-bold">{new Date(attendanceStatus.check_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                        {attendanceStatus.check_in_status && (
                          <span className={`text-[10px] font-bold uppercase ${
                            attendanceStatus.check_in_status === 'LATE' ? 'text-red-500' : 
                            attendanceStatus.check_in_status === 'EARLY' ? 'text-blue-500' : 
                            'text-green-500'
                          }`}>
                            {attendanceStatus.check_in_status.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Check Out</p>
                        <p className="text-xl font-bold">--:--</p>
                      </div>
                    </div>
                    <Button onClick={() => handlePunch('out')} disabled={isPunching} variant="destructive" className="w-full">
                      {isPunching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Check Out
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Check In</p>
                        <p className="text-xl font-bold">{new Date(attendanceStatus.check_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                        {attendanceStatus.check_in_status && (
                          <span className={`text-[10px] font-bold uppercase ${
                            attendanceStatus.check_in_status === 'LATE' ? 'text-red-500' : 
                            attendanceStatus.check_in_status === 'EARLY' ? 'text-blue-500' : 
                            'text-green-500'
                          }`}>
                            {attendanceStatus.check_in_status.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Check Out</p>
                        <p className="text-xl font-bold">{attendanceStatus.check_out ? new Date(attendanceStatus.check_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--'}</p>
                        {attendanceStatus.check_out_status && (
                          <span className={`text-[10px] font-bold uppercase ${
                            attendanceStatus.check_out_status === 'EARLY' ? 'text-orange-500' : 
                            attendanceStatus.check_out_status === 'LATE' ? 'text-blue-500' : 
                            'text-green-500'
                          }`}>
                            {attendanceStatus.check_out_status.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="bg-emerald-50 text-emerald-700 p-3 rounded-md text-sm text-center font-medium">
                      Attendance completed for today
                    </div>
                  </div>
                )}
                <Link to="/my-attendance" className="text-xs text-primary hover:underline block text-center pt-2 font-medium">Detailed Attendance Summary →</Link>
              </CardContent>
            </Card>

            <Card className="lg:col-span-7 shadow-sm border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Cake className="h-5 w-5 text-pink-500" />
                  Upcoming Celebrations
                </CardTitle>
                <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Next 30 Days</div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {celebrations.length > 0 ? (
                    celebrations.slice(0, 4).map((celeb) => (
                      <div key={`${celeb.id}-${celeb.type}`} className="flex items-center justify-between p-3 rounded-xl border border-slate-100 bg-slate-50/30 hover:bg-white hover:shadow-sm transition-all group">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full border-2 border-white shadow-sm overflow-hidden bg-slate-100 flex items-center justify-center font-bold text-slate-400">
                            {celeb.profile_picture ? (
                              <img src={`${process.env.REACT_APP_BACKEND_URL}${celeb.profile_picture}`} alt="" className="h-full w-full object-cover" />
                            ) : celeb.name[0]}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-800">{celeb.name}</p>
                            <p className="text-[10px] text-muted-foreground font-medium">{celeb.designation}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1.5 justify-end">
                            {celeb.type === 'BIRTHDAY' ? (
                              <Badge className="bg-pink-100 text-pink-700 hover:bg-pink-100 border-none text-[9px] px-2 py-0">BIRTHDAY</Badge>
                            ) : (
                              <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 border-none text-[9px] px-2 py-0">{celeb.years}Y ANNIV</Badge>
                            )}
                          </div>
                          <p className="text-[10px] font-bold text-slate-500 mt-1 uppercase tracking-tighter">
                            {new Date(celeb.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="py-10 text-center flex flex-col items-center gap-2">
                      <PartyPopper className="h-8 w-8 text-slate-200" />
                      <p className="text-xs text-muted-foreground">No celebrations this month</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
            {/* Announcement / Feed - Left Side */}
            <Card className="shadow-sm border-slate-200 flex flex-col overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between pb-4 bg-slate-50/50">
                <div className="space-y-1">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <PartyPopper className="h-5 w-5 text-orange-500" />
                    Company Feed
                  </CardTitle>
                </div>
                <Link to="/feeds" className="text-xs font-semibold text-primary hover:underline">View Wall</Link>
              </CardHeader>
              <CardContent className="flex-1 p-0">
                {feeds.length > 0 ? (
                  <div className="p-4 space-y-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-xs font-bold">
                        <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] text-primary overflow-hidden border border-slate-200">
                          {feeds[0].author_avatar ? (
                            <img
                              src={feeds[0].author_avatar.startsWith('http') ? feeds[0].author_avatar : `${process.env.REACT_APP_BACKEND_URL}${feeds[0].author_avatar}`}
                              alt=""
                              className="h-full w-full object-contain"
                            />
                          ) : (
                            feeds[0].author_name?.split(' ').map(n => n[0]).join('')
                          )}
                        </div>
                        <span className="text-slate-700">{feeds[0].author_name}</span>
                      </div>
                      <span className="text-[10px] text-muted-foreground">{new Date(feeds[0].created_at).toLocaleDateString()}</span>
                    </div>

                    <div
                      className="text-sm prose prose-slate max-w-none dashboard-feed-content"
                      dangerouslySetInnerHTML={{ __html: feeds[0].content }}
                    />
                  </div>
                ) : (
                  <div className="h-full min-h-[150px] text-center py-10 flex flex-col items-center justify-center bg-slate-50/30">
                    <p className="text-xs text-muted-foreground">No recent feed activities</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Latest Payslip Summary - Right Side */}
            <Card className="shadow-sm border-slate-200 bg-primary/5">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <IndianRupee className="h-5 w-5 text-emerald-600" />
                  Latest Payslip
                </CardTitle>
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-primary"
                    onClick={() => setShowSalary(!showSalary)}
                    title={showSalary ? "Hide sensitive data" : "Show sensitive data"}
                  >
                    {showSalary ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Link to="/my-payslips" className="text-xs font-semibold text-primary hover:underline">All Slips</Link>
                </div>
              </CardHeader>
              <CardContent>
                {latestPayslip ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between px-2">
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-muted-foreground uppercase">{latestPayslip.pay_period}</span>
                        <span className="text-[10px] text-slate-400 font-medium">Paid for {latestPayslip.paid_days} days</span>
                      </div>
                      <Badge className="bg-emerald-600 text-[10px] px-3 py-1 rounded-full">PAID</Badge>
                    </div>

                    <div className="bg-white p-6 rounded-2xl border shadow-sm space-y-6">
                      <div className="flex items-center justify-between pb-4 border-b border-slate-50">
                        <div className="space-y-1">
                          <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Gross Monthly</p>
                          <p className={cn("text-lg font-bold text-slate-700 transition-all duration-300", !showSalary && "blur-md select-none")}>
                            {formatIndianCurrency(latestPayslip.gross_salary || 0)}
                          </p>
                        </div>
                        <div className="text-right space-y-1">
                          <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider">Net Take Home</p>
                          <p className={cn("text-3xl font-black text-slate-900 transition-all duration-300", !showSalary && "blur-md select-none")}>
                            {formatIndianCurrency(latestPayslip.net_salary || 0)}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-[11px]">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center text-slate-500">
                            <span>Basic</span>
                            <span className={cn("font-bold text-slate-900 transition-all duration-300", !showSalary && "blur-md select-none")}>
                              {formatIndianCurrency(latestPayslip.basic || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-slate-500">
                            <span>HRA</span>
                            <span className={cn("font-bold text-slate-900 transition-all duration-300", !showSalary && "blur-md select-none")}>
                              {formatIndianCurrency(latestPayslip.hra || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-slate-500">
                            <span>Spl. Allowance</span>
                            <span className={cn("font-bold text-slate-900 transition-all duration-300", !showSalary && "blur-md select-none")}>
                              {formatIndianCurrency(latestPayslip.special_allowance || 0)}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-2 border-l border-slate-50 pl-8">
                          <div className="flex justify-between items-center text-slate-500">
                            <span>PF (Emp)</span>
                            <span className={cn("font-bold text-red-600 transition-all duration-300", !showSalary && "blur-md select-none")}>
                              -{formatIndianCurrency(latestPayslip.pf_employee || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-slate-500">
                            <span>TDS</span>
                            <span className={cn("font-bold text-red-600 transition-all duration-300", !showSalary && "blur-md select-none")}>
                              -{formatIndianCurrency(latestPayslip.tds || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-slate-500">
                            <span>Other Ded.</span>
                            <span className={cn("font-bold text-red-600 transition-all duration-300", !showSalary && "blur-md select-none")}>
                              -{formatIndianCurrency(latestPayslip.professional_tax + (latestPayslip.salary_advance_recovery || 0))}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-dashed">
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] font-bold text-slate-400 uppercase">Total Deductions</span>
                          <span className={cn("text-sm font-bold text-red-700 transition-all duration-300", !showSalary && "blur-md select-none")}>
                            -{formatIndianCurrency(latestPayslip.total_deductions || 0)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => handleDownloadPayslip(latestPayslip.run_id)}
                      className="w-full flex items-center justify-center gap-2 text-primary font-bold text-xs hover:underline pt-1 rounded-xl group transition-all"
                    >
                      <Download className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
                      Download Detailed PDF
                    </button>
                  </div>
                ) : (
                  <div className="h-full text-center py-10 flex flex-col items-center justify-center border border-dashed rounded-xl bg-slate-50/50">
                    <p className="text-xs text-muted-foreground">No payslips available yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-7">
            {/* New Colleagues - Left 3 cols */}
            <Card className="lg:col-span-3 shadow-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  Welcome New Joiners
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="px-6 pb-6 space-y-4">
                  {newHires.length > 0 ? (
                    newHires.slice(0, 5).map((emp) => (
                      <div key={emp.id} className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 border border-slate-200 overflow-hidden">
                          {emp.profile_picture ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${emp.profile_picture}`} alt={emp.first_name} className="h-full w-full object-cover" />
                          ) : emp.first_name?.[0]}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold truncate leading-none">{emp.first_name} {emp.last_name}</p>
                          <p className="text-[10px] text-muted-foreground mt-1 truncate">{emp.designation || 'New Member'}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-muted-foreground italic py-4 text-center">Everyone is settled in!</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Upcoming Holidays - Right 4 cols */}
            <Card className="lg:col-span-4 shadow-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-primary" />
                  Holidays this Month
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 px-6 pb-6">
                <div className="space-y-4">
                  {getUpcomingHolidays().length > 0 ? (
                    getUpcomingHolidays().map((holiday) => (
                      <div key={holiday.id} className="flex items-center justify-between p-3 rounded-lg bg-orange-50/30 border border-orange-100">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-bold text-slate-800">{holiday.name}</p>
                          <p className="text-[10px] text-muted-foreground font-medium">
                            {new Date(holiday.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-[9px] uppercase bg-white border-orange-200 text-orange-700 ml-2">{holiday.is_optional ? 'Optional' : 'Public'}</Badge>
                      </div>
                    ))
                  ) : (
                    <div className="py-8 flex flex-col items-center gap-2 text-center border border-dashed rounded-xl bg-slate-50/50">
                      <CalendarDays className="h-8 w-8 text-muted-foreground/30" />
                      <p className="text-[10px] text-muted-foreground font-medium">Enjoy the hustle! No holidays for 30 days.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Leave Status Table */}
          <Card className="shadow-sm border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">My Recent Leaves</CardTitle>
              <Link to="/my-leaves" className="text-xs font-semibold text-primary hover:underline">Apply Leave →</Link>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left border-t border-slate-100">
                  <thead className="text-[10px] text-muted-foreground bg-slate-50 uppercase font-black">
                    <tr>
                      <th className="px-6 py-4">Type</th>
                      <th className="px-6 py-4">Duration</th>
                      <th className="px-6 py-4 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {recentLeaves.length > 0 ? (
                      recentLeaves.map((leave) => (
                        <tr key={leave.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-6 py-4 font-bold text-slate-700">{leave.leave_type_name}</td>
                          <td className="px-6 py-4 text-xs font-medium text-muted-foreground">
                            {new Date(leave.start_date).toLocaleDateString()} - {new Date(leave.end_date).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <Badge variant={leave.status === 'APPROVED' ? 'default' : leave.status === 'REJECTED' ? 'destructive' : 'secondary'} className="text-[9px] uppercase tracking-wider font-black">
                              {leave.status}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="px-6 py-12 text-center text-muted-foreground italic text-xs">No recent leave requests on record.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm border-slate-200">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-primary" />
                Leave Balances
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 divide-x divide-y border-t">
                {leaveBalance?.balances?.map((balance) => (
                  <div key={balance.leave_type_id} className="p-4 text-center">
                    <p className="text-[10px] font-bold text-muted-foreground uppercase truncate">{balance.leave_type_name.split(' ')[0]}</p>
                    <p className="text-lg font-black text-slate-800">{balance.balance}</p>
                    <p className="text-[8px] text-slate-400 font-bold uppercase">Days</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div >
      )}

      {/* Quick Actions */}
      <div className="pt-2">
        <h3 className="text-sm font-semibold text-muted-foreground mb-4">Quick Links</h3>
        <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
          {isAdminOrHR() ? (
            <>
              <QuickLink href="/employees" icon={Users} label="Employees" />
              <QuickLink href="/attendance" icon={Briefcase} label="Attendance" />
              <QuickLink href="/leave" icon={CalendarDays} label="Leaves" />
              <QuickLink href="/payroll" icon={IndianRupee} label="Payroll" />
              <QuickLink href="/onboarding" icon={UserPlus} label="Onboarding" />
              <QuickLink href="/settings" icon={Settings} label="Settings" />
            </>
          ) : (
            <>
              <QuickLink href="/my-attendance" icon={Briefcase} label="My Attendance" />
              <QuickLink href="/my-leaves" icon={CalendarDays} label="My Leaves" />
              <QuickLink href="/my-payslips" icon={IndianRupee} label="My Payslips" />
              <QuickLink href="/my-tax" icon={TrendingUp} label="Tax" />
              <QuickLink href="/employee-directory" icon={Users} label="Directory" />
              <QuickLink href="/my-profile" icon={User} label="Profile" />
            </>
          )}
        </div>
      </div>
    </div >
  );
};

const QuickLink = ({ href, icon: Icon, label }) => (
  <Link to={href} className="flex flex-col items-center p-4 rounded-lg border hover:bg-slate-50 transition-colors group">
    <Icon className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors mb-2" />
    <span className="text-xs font-medium text-center">{label}</span>
  </Link>
);

export default Dashboard;
